import { addDays, format, subDays } from 'date-fns'
import { useEffect, useState } from 'react'

import { useGetClientMarketInstrumentPriceHistory } from '@/gen/publicApi'

import { GRAPH_DATE_FORMAT, fromUtc } from '@/utils/time'

import HistoryChart, { DataPoint } from './chart'

interface Props {
    clientCode: string
    marketCode: string
    instrumentSymbol: string
    dayValue: number
}

export default function DailyHistory({ dayValue, clientCode, marketCode, instrumentSymbol }: Props) {
    const priceHistory = useGetClientMarketInstrumentPriceHistory(clientCode, marketCode, instrumentSymbol, dayValue, {
        query: { refetchInterval: 10000 },
    })

    const [data, setData] = useState<DataPoint[]>([])
    useEffect(() => {
        // If the value is the same as the data, ignore. Nothing to do
        if (dayValue === data.length) {
            return
        }

        if (priceHistory.isLoading && data.length) {
            // We're reloading the data from the server, so clear the local data
            setData([])
        }

        if (!priceHistory.data) {
            return
        }

        const mappedData = priceHistory.data.map(p => ({
            time: format(fromUtc(p.localTradingDate), GRAPH_DATE_FORMAT),
            price: p.close,
        }))

        // Fill with empty days if there is no data for a day
        const daysAgo = subDays(new Date(), dayValue)
        const filledData = Array.from({ length: dayValue }, (_, i) => {
            const day = format(addDays(daysAgo, i), GRAPH_DATE_FORMAT)
            const existingData = mappedData.find(d => d.time === day)
            return existingData || { time: day, price: null }
        })

        setData(filledData)
    }, [data, dayValue, priceHistory.data, priceHistory.isLoading])

    if (priceHistory.isLoading || !priceHistory.data) {
        return null
    }

    return <HistoryChart data={data} />
}
